import { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import { extractTheme, PartnerContextProps, partnerDefault } from '@/partner';
import { css } from '@emotion/css';
import { GlobalStyles as CondoDSStyles } from 'condolivre-ds';
import { createGlobalStyle } from 'styled-components';

const PartnerContext = createContext<PartnerContextProps>(partnerDefault);

export const PartnerProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const partner: PartnerContextProps = useMemo(
        () => extractTheme(window?.location?.hostname),
        [window?.location?.hostname]
    );

    const OverrideGlobalStyle = createGlobalStyle`
        body {
            background: ${partner.root.background};
        }
    `;

    const OverrideContentStyle = css(`
                    & .title-stroke {
                        &:before {
                            background: ${partner.root.title.stroke};
                        }
                        &--supply:before {
                            background: ${partner.root.title.stroke_supply};
                        }
                        &__admpanel {
                            color: ${partner.root.title.textColor}!important;
                        }
                    }
                    & .root-footer {
                        background: ${partner.root.footer.backgroundColor};
                        border-bottom-color: ${partner.pages.login.backgroundColor};
                        & .footerText {
                            border-bottom-color: ${partner.root.footer.separatorColor};
                        }
                    }
                    & .admpanel-sidebar {
                        &.active {
                            & .menu__item {
                                &.menu__active {
                                    background: ${partner.root.sidebar.mobile.listBackgroundColor};
                                    span {
                                        color: ${partner.root.sidebar.mobile.listTextColor};
                                    }        
                                }
                                &.menu__selected {
                                    background-color: ${partner.root.sidebar.mobile.activeBackgroundColor};
                                    span {
                                        color: ${partner.root.sidebar.mobile.activeTextColor};
                                    }
                                }
                            }
                        }
                    
                        & > a:first-of-type {
                            display: none;
                        }
                        
                        & .admpanel-logo {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            border: 0;
                            @media (max-width: 991px) {
                                background: ${partner.root.sidebar.mobile.backgroundColor};
                                padding: 0.8rem 0;
                            }
                            @media(min-width: 992px){
                                margin: 0.8rem 0; 
                            }
                        }
                        
                        & .admpanel-sideBarTitle {
                            @media (max-width: 991px) {
                                background: ${partner.root.sidebar.mobile.accentColor};
                            }
                        }
                    }
                    & .header-menu {
                        @media (max-width: 991px) {
                            margin-top: 1rem;
                        }
                        &__badge {
                            & > div {
                                & > div:nth-child(1) {
                                    background: ${partner.root.header.badge.image.backgroundColor};
                                    span {
                                        color: ${partner.root.header.badge.image.textColor}!important;
                                    }
                                }
                                & > div:nth-child(2) > div {
                                    color: ${partner.root.header.badge.content.textColor}!important;
                                }
                            }
                        }
                    }
                    & .page-login {
                        background: ${partner.pages.login.backgroundColor}!important;
                    }
                    & .page-reset {
                        background: ${partner.pages.login.backgroundColor}!important;
                    }
                    & .indica-ad {
                        &__title {
                            font-size: ${partner.root.condoindica.fontSize}!important;
                        }
                        &__image {
                            background-image: url("${partner.root.condoindica.url}");
                        }
                    }
                    & .page-indica {
                        &__image {
                            background-image: url("${partner.root.condoindica.url}");
                        }
                        &__cta {
                            > div {
                                > div:nth-child(2) {
                                    background: ${partner.pages.indica.cta.backgroundColor};
                                }
                            }
                        }
                        &__title {
                            font-weight: 400;
                            span {
                                font-weight: 700;
                            }
                        }
                    }
            `);

    return (
        <PartnerContext.Provider value={partner}>
            <>
                <OverrideGlobalStyle />
                <CondoDSStyles theme={partner.theme}>
                    <div className={OverrideContentStyle}>{children}</div>
                </CondoDSStyles>
            </>
        </PartnerContext.Provider>
    );
};

export const usePartner = () => {
    const context = useContext(PartnerContext);
    if (!context) {
        return partnerDefault;
    }
    return context;
};
